import { IAgent, Presenter } from '@d-id/types';
import { Theme } from '@emotion/react';
import React from 'react';

export interface CssProps extends React.CSSProperties {
    color?: any;
    [key: string]: any;
}

export type SxType = CssProps | ((theme: Theme) => CssProps);

export type StyleCollection = Record<string, SxType>;

export type ValueOrUpdater<T> = T | ((prev: T) => T);

export type MaybePromise<T> = T | Promise<T>;

export enum AppRoutes {
    AccountSettings = '/account-settings',
    AdvancedEditor = '/editor2',
    Agent = '/agents',
    AgentEditor = '/agents/editor',
    AgentShare = '/agents/share',
    AgentTemplate = '/agents/template',
    Apps = '/apps',
    Avatars = '/avatars',
    AvatarsCreate = '/avatars/create',
    AvatarsCreatePremiumPlus = '/avatars/create/premium-plus',
    AvatarsCreateExpress = '/avatars/create/express',
    AvatarEditPage = '/avatars/draft/edit',
    AvatarsCreateSuccess = '/avatars/create/success',
    AvatarCreatePremiumPlusSuccessPage = '/avatars/create/premium-plus/success',
    Backoffice = '/backoffice',
    CampaignCreate = '/campaigns/create',
    CampaignLandingPage = '/campaign',
    Campaigns = '/campaigns',
    Checkout = '/checkout',
    Docs = '/docs',
    Friends = '/friends',
    Home = '/',
    Insights = '/insights',
    InsightsShare = '/insights/share',
    Login = '/login',
    PostCheckout = '/post-checkout',
    PostSso = '/post-sso',
    Share = '/share',
    Support = '/support',
    MobileHomepage = '/mobile-homepage',
    Translation = '/video-translate',
    TranslationDraft = '/video-translate/draft',
    Unsupported = '/unsupported',
    VideoLibrary = '/video-library',
    VideoStudio = '/video-studio',
}

export type AppPathname = (typeof AppRoutes)[keyof typeof AppRoutes];

// some names are blank on purpose since those pages has no "meaning" and we don't want a user to see them
export const AppRouteNames: Record<AppPathname, string> = {
    '/account-settings': 'Settings',
    '/editor2': 'Editor',
    '/agents': 'Agents',
    '/agents/editor': 'Agents',
    '/agents/share': 'Agents',
    '/agents/template': 'Agents',
    '/apps': 'Apps',
    '/avatars': 'Avatars',
    '/avatars/create': 'Avatars',
    '/avatars/create/premium-plus': 'Avatars',
    '/avatars/draft/edit': 'Avatars',
    '/avatars/create/success': 'Avatars',
    '/avatars/create/premium-plus/success': 'Avatars',
    '/avatars/create/express': 'Avatars',
    '/backoffice': 'Backoffice',
    '/campaigns/create': 'Campaigns',
    '/campaign': 'Campaigns',
    '/campaigns': 'Campaigns',
    '/checkout': 'Checkout',
    '/docs': 'Docs',
    '/friends': 'Friends',
    '/': 'Home',
    '/insights/share': 'Insights',
    '/insights': 'Insights',
    '/login': 'Login',
    '/post-checkout': '',
    '/post-sso': '',
    '/share': 'Share',
    '/support': 'Support',
    '/mobile-homepage': 'Home',
    '/video-translate/draft': 'Edit Translation',
    '/video-translate': 'Translate',
    '/unsupported': '',
    '/video-library': 'Video Library',
    '/video-studio': 'Video Studio',
} as const;

export enum CropMode {
    Wide = 'Wide',
    Square = 'Square',
    Circle = 'Circle',
    Vertical = 'Vertical',
    Fit = 'Fit',
}

export enum Emotion {
    Natural = 'natural',
    Happy = 'happy',
    Serious = 'serious',
    Surprise = 'surprise',
}

export interface LocationState {
    isOwner?: boolean;
    agent?: IAgent;
    clientKey?: string;
    from?: string;
    fromPath?: string;
    wasEmbedded?: boolean;
    initialAvatar?: Presenter;
}

export interface ExtendedLocation {
    state?: LocationState;
    pathname?: string;
}

export interface WordLevelTimestamp {
    startTime: number;
    endTime: number;
    word: string;
}

export enum MixPanelEvent {
    VIEW = 'view',
    CLICK = 'click',
    SUCCESS = 'success',
    ERROR = 'error',
    CANCEL = 'cancel',
}

export const allowedLanguages = [
    'english',
    'spanish',
    'italian',
    'swedish',
    'dutch',
    'german',
    'french',
    'filipino',
    'portuguese',
    'turkish',
    'czech',
    'croatian',
    'indonesian',
    'polish',
    'romanian',
    'danish',
    'slovak',
    'malay',
] as const;

export type SupportedLanguage = (typeof allowedLanguages)[number];
